<template>
  <Content
    class="order-import"
    :header-label="t('common.importParam', { param: t('orders.orders', 2) }, 2)"
    :back-btn-label="t('common.backToParam', { param: t('orders.orders', 2) })"
    :back-btn-route="{ name: 'orderList' }"
    :show-toolbar="
      uploadState === 'NOT_STARTED' || jobExecutionDetails?.isFinalState()
    "
    remove-aside-card-padding
    remove-card-padding
  >
    <template #headerRight>
      <RygenButton
        :label="t('batchImport.downloadCsvTemplate')"
        icon="pi pi-download"
        @click="onDownloadTemplate"
      />
    </template>

    <template #toolbar>
      <div
        role="toolbar"
        class="edit-bar flex flex-gap-1 justify-content-center p-3"
      >
        <div class="edit-bar-buttons flex flex-gap-1">
          <template v-if="!!jobExecutionDetails?.isFinalState()">
            <RygenButton
              as="router-link"
              :to="{ name: 'importOrders' }"
              :label="t('batchImport.uploadAnotherFile')"
              juicy
            />
            <RygenButton
              as="router-link"
              mode="tertiary"
              :to="{ name: 'orderList' }"
              :label="
                t('common.backToParam', {
                  param: t('orders.orders', 2),
                })
              "
              juicy
            />
          </template>
          <RygenButton
            v-if="uploadState === 'NOT_STARTED'"
            mode="primary"
            :disabled="!uploadFile || !selectedClient"
            :label="t('batchImport.beginProcessing')"
            icon="pi pi-upload"
            juicy
            @click="onProcess"
          />
        </div>
        <div
          v-if="uploadState === 'NOT_STARTED'"
          class="edit-bar-toggle flex flex-gap-1 align-items-center pointer-events-auto"
        >
          <InputSwitch
            id="file-contains-header"
            v-model="shouldSkipHeaderRow"
          />
          <label for="file-contains-header" class="cursor-pointer">
            {{ t('batchImport.fileContainsHeaderRow') }}
          </label>
        </div>
      </div>
    </template>

    <div v-show="!processing">
      <div
        class="detail-section flex flex-column flex-gap-1 align-items-center"
      >
        <div class="upload-container w-10">
          <FileUpload
            ref="fileUploader"
            :show-cancel-button="false"
            :show-upload-button="false"
            custom-upload
            :multiple="false"
            :file-limit="1"
            :choose-label="
              t('common.chooseParam', { param: t('common.files', 1) })
            "
            accept="text/csv"
            @select="onFileSelected"
            @remove="onFileRemoved"
          >
            <template #empty>
              <FontAwesomeIcon
                :icon="['fas', 'cloud-upload']"
                class="upload-icon"
              />
              <p class="upload-instructions text-gray-darkest">
                {{ t('batchImport.dragYourCsvFileHere') }}
              </p>
            </template>
          </FileUpload>
        </div>
      </div>
    </div>

    <BatchImportJobStatusSection
      v-show="processing"
      :job-status="jobExecutionDetails"
      :upload-state="uploadState"
      :process-state="processState"
      :error-document="errorDocument.document"
      :loading-error-document="errorDocument.loading"
    />

    <template #aside>
      <CsvSpecificationSection
        :loading="specification.loading"
        :specification="specification.specification"
      />
    </template>

    <ChangeDomainModal
      v-model:visible="isChangeDomainModalVisible"
      :selected-domain="selectedClient"
      :selectable-domain-types="[DomainType.Client]"
      :domain-options="clients.data"
      :header="t('common.selectParam', { param: t('clients.clients') })"
      position="center"
      closable
      @change-domain="onClientChange"
    />
  </Content>
</template>

<script lang="ts" setup>
import axios from 'axios';
import {
  computed,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  Ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useToast } from '@rygen/rygen-ui/composables';
import {
  BatchJobExecutionDetails,
  DomainReference,
  DomainTree,
  DomainType,
} from '@rygen/rygen-ui/model';

import apiInstance from '@/api';
import {
  BatchImportStepState,
  CsvSpecification,
  DocumentReference,
  Order,
} from '@/model';
import { useClientStore } from '@/stores/Client';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useMessageStore } from '@/stores/Message';
import { useParentEntityStore } from '@/stores/ParentEntity';

import BatchImportJobStatusSection from '@//components/batchImport/BatchImportJobStatusSection.vue';
import CsvSpecificationSection from '@//components/batchImport/CsvSpecificationSection.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import ClientAvatar from '@rygen/rygen-ui/components/client/ClientAvatar.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import ChangeDomainModal from '@rygen/rygen-ui/components/domain/ChangeDomainModal.vue';
import {
  default as FileUpload,
  FileUploadSelectEvent,
} from 'primevue/fileupload';
import InputSwitch from 'primevue/inputswitch';

const OWNING_CLIENT_MESSAGE_KEY = 'order-import-owning-client-message';

const props = defineProps({
  jobId: {
    type: Number,
    default: undefined,
    required: false,
  },
});

const { jobId } = toRefs(props);

const router = useRouter();
const { t } = useI18n();
const toast = useToast();
const { clientTreeMap, clients, loadClients } = useClientStore();
const { addMessage, removeMessage } = useMessageStore();
const { selectedDomain } = useCurrentUserStore();
const { clearParentEntity, setParentEntity } = useParentEntityStore();

const isChangeDomainModalVisible = ref(false);
const selectedClient: Ref<DomainTree | undefined> = ref();
const fileUploader: Ref<typeof FileUpload | null> = ref(null);
const uploadFile: Ref<File | undefined> = ref(undefined);
const shouldSkipHeaderRow = ref(true);
const jobExecutionDetails: Ref<BatchJobExecutionDetails | undefined> =
  ref(undefined);
const uploadState: Ref<BatchImportStepState> = ref('NOT_STARTED');
const processState: Ref<BatchImportStepState> = ref('NOT_STARTED');
const errorDocument = ref({
  loading: false,
  document: undefined as DocumentReference | undefined,
});
const specification = ref({
  loading: false,
  specification: undefined as CsvSpecification | undefined,
});

const resetState = () => {
  (fileUploader?.value as any)?.clear();
  jobExecutionDetails.value = undefined;
  processState.value = 'NOT_STARTED';
  uploadState.value = 'NOT_STARTED';
  uploadFile.value = undefined;
  errorDocument.value.document = undefined;
  errorDocument.value.loading = false;
};

const doFetchJobStatus = async (jobId: number): Promise<void> => {
  if (uploadState.value === 'NOT_STARTED') {
    uploadState.value = 'COMPLETE';
  }

  if (processState.value === 'NOT_STARTED') {
    processState.value = 'IN_PROGRESS';
  }

  jobExecutionDetails.value =
    await apiInstance.orderImport.getImportStatus(jobId);

  if (!jobExecutionDetails.value) {
    resetState();
    return;
  }

  if (jobExecutionDetails.value.isFinalState()) {
    processState.value = 'COMPLETE';
  } else {
    setTimeout(() => doFetchJobStatus(jobId), 2000);
  }

  if (
    jobExecutionDetails.value.status === 'COMPLETED' &&
    jobExecutionDetails.value.errorCount &&
    jobExecutionDetails.value.errorCount > 0
  ) {
    errorDocument.value.document = await fetchErrorDocument(jobId);
  } else if (jobExecutionDetails.value.status === 'FAILED') {
    toast.addError({
      detail: t('errors.importingParam', {
        param: t('batchImport.uploadDocument'),
      }),
      error: new Error(jobExecutionDetails.value.status),
    });
  }
};

watch(
  () => props.jobId,
  () => {
    if (!jobId?.value) {
      resetState();
      return;
    }

    doFetchJobStatus(jobId.value);
  },
  { immediate: true },
);

const processing = computed(
  () =>
    uploadState.value !== 'NOT_STARTED' ||
    processState.value !== 'NOT_STARTED' ||
    !!jobExecutionDetails.value,
);

const onFileSelected = (event: FileUploadSelectEvent) => {
  uploadFile.value = event.files?.[0];
};

const onFileRemoved = () => {
  uploadFile.value = undefined;
};

const onDownloadTemplate = async () => {
  if (!selectedClient.value?.id) {
    return;
  }

  const blob = await apiInstance.orderImport.fetchTemplate();
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = 'OrderImportTemplate.csv';
  link.click();
};

const onProcess = async () => {
  if (!uploadFile.value) {
    return;
  }

  const documentReference = await doUpload(uploadFile.value);

  jobExecutionDetails.value = await doImport(documentReference);

  router.push({
    name: 'importOrdersJobStatus',
    params: {
      jobId: jobExecutionDetails.value.jobId,
    },
  });
};

const doUpload = async (file: File): Promise<DocumentReference> => {
  const documentReference = new DocumentReference({
    entityType: 'BATCH_UPLOAD',
    fileName: file.name,
    contentType: 'text/csv',
  });

  try {
    uploadState.value = 'IN_PROGRESS';

    const response =
      await apiInstance.orderImport.getUploadUrl(documentReference);

    await axios.put(response.uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });

    uploadState.value = 'COMPLETE';

    return new DocumentReference({
      ...documentReference,
      fileName: response.fileName,
      signedUrl: response.downloadUrl,
    });
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return Promise.reject('request canceled');
    }

    console.error('failed to upload order batch import file', e);
    toast.addError({
      error: e,
      detail: t('errors.uploadState', {
        param: t('common.files', 1),
      }),
    });

    uploadState.value = 'ERROR';
    throw e;
  }
};

const doImport = async (
  documentReference: DocumentReference,
): Promise<BatchJobExecutionDetails> => {
  try {
    processState.value = 'IN_PROGRESS';

    if (!documentReference.fileName) {
      throw Error('Filename cannot be empty');
    }

    if (!selectedClient.value?.id) {
      throw Error('Selected client cannot be empty');
    }

    const jobExecutionDetails = await apiInstance.orderImport.import(
      documentReference.fileName,
      shouldSkipHeaderRow.value,
      selectedClient.value.id,
    );

    if (jobExecutionDetails.isFinalState()) {
      processState.value = 'COMPLETE';
    }

    return jobExecutionDetails;
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return Promise.reject('request canceled');
    }

    console.error('failed to process order batch import file', e);
    toast.addError({
      error: e,
      detail: t('errors.processState', {
        param: t('orders.orders', 2),
      }),
    });

    processState.value = 'ERROR';
    throw e;
  }
};

const fetchErrorDocument = async (
  jobId: number,
): Promise<DocumentReference> => {
  try {
    errorDocument.value.loading = true;
    return await apiInstance.orderImport.getErrorDocument(jobId);
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return Promise.reject('request canceled');
    }

    console.error('failed to load error document', e);
    toast.addError({
      error: e,
      detail: t('errors.loadingParam', {
        param: `${t('orders.orders', 2)} ${t('common.error')} ${t('common.files', 1)}`,
      }),
    });
    throw e;
  } finally {
    errorDocument.value.loading = false;
  }
};

const loadSpecification = async () => {
  try {
    if (!selectedClient.value?.id) {
      return;
    }

    specification.value.loading = true;
    specification.value.specification =
      await apiInstance.orderImport.fetchSpecification();
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('failed to load CSV Specification', e);
    toast.addError({
      error: e,
      detail: t('errors.loadingParam', {
        param: `${t('orders.orders', 2)} ${t('batchImport.csvSpecification')}`,
      }),
    });
    throw e;
  } finally {
    specification.value.loading = false;
  }
};

const onClientChange = (newClient: DomainReference) => {
  selectedClient.value = newClient.id
    ? clientTreeMap.value.get(newClient.id)
    : undefined;

  router.push({ name: 'importOrders' });
};

const updateSelectedClient = (selectedDomain: DomainReference | undefined) => {
  selectedClient.value =
    selectedDomain?.domainType === DomainType.Client && selectedDomain.id
      ? clientTreeMap.value.get(selectedDomain.id)
      : undefined;

  isChangeDomainModalVisible.value = !selectedClient.value;
};

const loadPage = async (domainToLoad: DomainReference | undefined) => {
  loadSpecification();
  await loadClients();
  updateSelectedClient(domainToLoad);
};

watch(selectedDomain, loadPage);

watch(
  selectedClient,
  async (newVal: DomainTree | undefined) => {
    if (newVal) {
      loadSpecification();

      const clientOwnedOrder = ref(
        new Order({
          owner: newVal?.asDomainReference(),
        }),
      );
      setParentEntity(clientOwnedOrder);

      addMessage({
        id: OWNING_CLIENT_MESSAGE_KEY,
        severity: 'info',
        text: t('common.assignedToParam', { param: newVal.name }),
        iconSlot: () =>
          h(ClientAvatar, {
            name: newVal.name,
            logo: newVal.branding?.avatar?.signedUrl,
          }),
        onClick: () => (isChangeDomainModalVisible.value = true),
      });
    }
  },
  { immediate: true },
);

watch(isChangeDomainModalVisible, (newVal: boolean, oldVal: boolean) => {
  if (oldVal && !newVal && !selectedClient.value) {
    router.back();
  }
});

onBeforeUnmount(() => {
  clearParentEntity();
  removeMessage(OWNING_CLIENT_MESSAGE_KEY);
});

onMounted(async () => {
  await loadPage(selectedDomain.value);
});
</script>

<style>
.order-import {
  .upload-container {
    .p-fileupload {
      .p-fileupload-content {
        .p-progressbar {
          display: none;
        }
      }
    }
  }
}
</style>
